import React, { forwardRef, useImperativeHandle, useRef } from "react";
import Select, { components } from "react-select";
import { withAsyncPaginate } from "react-select-async-paginate";
import AsyncSelect from "react-select/async";
import { colors } from "./colors";
import styled from "styled-components";
import CreatableSelect from "react-select/creatable";

const StyledDownIconWrapper = styled.div`
    display: flex;
    align-items: center;
    color: ${colors.neutral[900]};
    margin-right: 4px;
`;

const StyledCancelIconWrapper = styled.div`
    display: flex;
    align-items: center;
    color: ${(props) => colors[props.variant || "primary"][900]};
    margin-right: 8px;
    cursor: pointer;
`;

export const StyledCheckbox = styled.input.attrs({ type: "checkbox" })`
    appearance: none;
    width: 20px;
    height: 20px;
    border: 1px solid ${colors.neutral[500]};
    border-radius: 4px;
    opacity: 1;
    outline: none;
    cursor: pointer;
    transition: all 0.2s ease;

    &:checked {
        background-color: ${(props) => colors[props.variant || "primary"][900]};
        border-color: ${(props) => colors[props.variant || "primary"][900]};
    }

    &:checked::after {
        content: "✔";
        color: white;
        font-size: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    &:hover {
        border-color: ${(props) => colors[props.variant || "primary"][700]};
    }

    position: relative;
`;

const StyledHighlightLetter = styled.b`
    color: ${(props) => colors[props.variant || "primary"][900]};
`;

const customStyles = (variant, error) => ({
    control: (provided, state) => ({
        ...provided,
        border: !error
            ? state.isFocused
                ? `2px solid ${colors[variant][900]}`
                : `1px solid ${colors.neutral[300]}`
            : `1px solid #e74c3c`,
        borderRadius: "8px",
        boxSizing: "border-box",
        borderColor: "none",
        boxShadow: "none",
        padding: state.isFocused ? "0 7px" : "0 8px",
        minHeight: "40px",
        fontSize: "0.91563rem",
        fontWeight: 400,
        lineHeight: "1.2938rem",
        "&:hover": {
            borderColor: "none",
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: colors.neutral[500],
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: "8px",
        zIndex: 9999,
        position: "absolute",
        width: "100%",
        overflow: "hidden",
    }),
    option: (provided) => ({
        ...provided,
        color: colors.neutral[900],
        backgroundColor: "#fff",
        "&:hover": {
            backgroundColor: "#F6F8F9", //TODO: tekin-renk eşleştirmesini yapamadık eklenmesi gerekiyor
            color: colors.neutral[900],
        },
    }),
    valueContainer: (provided, state) => ({
        ...provided,
        padding: "0 5px",
        display: "flex",
        flexWrap: "wrap",
        gap: "4px",
    }),

    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
});

const customUnstyled = (variant) => ({
    control: (provided) => ({
        ...provided,
        backgroundColor: "transparent",
        border: "none",
        boxShadow: "none",
        minHeight: "unset",
        fontSize: "inherit",
        fontWeight: "inherit",
        lineHeight: "inherit",
    }),
    input: (base) => ({
        ...base,
        paddingBottom: 0,
        paddingTop: 0,
        margin: 0,
    }),
    placeholder: (base, state) => ({
        ...base,
        color: colors.neutral[500],
        textDecoration: state.selectProps.menuIsOpen ? "underline" : "none",
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: "8px",
        zIndex: 9999,
        position: "absolute",
        width: "100%",
        overflow: "hidden",
    }),
    option: (provided) => ({
        ...provided,
        color: colors.neutral[900],
        backgroundColor: "#fff",
        "&:hover": {
            backgroundColor: "#F6F8F9", //TODO: tekin-renk eşleştirmesini yapamadık eklenmesi gerekiyor
            color: colors.neutral[900],
        },
    }),
    valueContainer: (base) => ({ ...base, padding: 0 }),
    singleValue: (base, state) => ({
        ...base,
        marginLeft: 0,
        color: "inherit",
        textDecoration: state.selectProps.menuIsOpen ? "underline" : "none",
    }),
    dropdownIndicator: (base) => ({ ...base, padding: 0 }),
    clearIndicator: (base) => ({ ...base, padding: 0 }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
});

const getHighlightLetter = (label, userInput, variant) => {
    const regex = new RegExp(`(${userInput})`, "gi");

    return label.split(regex).map((part, index) =>
        regex.test(part) ? (
            <StyledHighlightLetter key={index} variant={variant}>
                {part}
            </StyledHighlightLetter>
        ) : (
            part
        )
    );
};

const Option = ({ variant, ...props }) => {
    const userInput = props?.selectProps?.inputValue || "";
    const label = props?.data?.label || "";

    const isMatch = label.toLowerCase().includes(userInput.toLowerCase());

    return isMatch ? (
        <div>
            <components.Option {...props}>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        {userInput?.length ? getHighlightLetter(label, userInput, variant) : label}{" "}
                        {props.isSelected && "✓"}{" "}
                    </div>
                    {props.isMulti && (
                        <StyledCheckbox variant={variant} checked={props.isSelected} onChange={() => null} />
                    )}
                </div>
            </components.Option>
        </div>
    ) : null;
};

const CreatableAsyncPaginateComp = withAsyncPaginate(CreatableSelect);

// Tam eşleşme için sıralama fonksiyonu
const sortByExactMatch = (options, inputValue) => {
    if (!inputValue || !Array.isArray(options)) return options;

    const lowerInput = inputValue.toLowerCase().trim();

    return options.sort((a, b) => {
        const aLabel = (a.label || "").toLowerCase().trim();
        const bLabel = (b.label || "").toLowerCase().trim();

        const aExactMatch = aLabel === lowerInput;
        const bExactMatch = bLabel === lowerInput;

        if (aExactMatch && !bExactMatch) return -1;
        if (!aExactMatch && bExactMatch) return 1;

        const aStartsWith = aLabel.startsWith(lowerInput);
        const bStartsWith = bLabel.startsWith(lowerInput);

        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        return aLabel.localeCompare(bLabel);
    });
};

const CustomSelect = forwardRef(
    (
        {
            classNamePrefix = "customSelectInput",
            value,
            unstyled = false,
            showSelectedOptions,
            variant = "primary",
            error = false,
            ...props
        },
        ref
    ) => {
        const selectRef = useRef(null);
        const containerRef = useRef(null);

        useImperativeHandle(
            ref,
            () => ({
                selectRef,
            }),
            [selectRef]
        );

        const handleChange = (value, actionMeta) => {
            if (actionMeta?.action === "create-option") {
                props.onChange(value);
            } else {
                props.onChange(value);
            }
        };

        const renderDropdownIndicator = () => (
            <StyledDownIconWrapper aria-label="chevron-down" role="img">
                <span className="material-symbols-outlined">keyboard_arrow_down</span>
            </StyledDownIconWrapper>
        );

        const renderClearIndicator = ({ innerProps: { ref, ...restInnerProps } }) => (
            <StyledCancelIconWrapper {...restInnerProps} ref={ref} aria-label="x" role="img" variant={variant}>
                <span className="material-symbols-outlined">cancel</span>
            </StyledCancelIconWrapper>
        );

        const commonComponents = {
            DropdownIndicator: renderDropdownIndicator,
            ClearIndicator: renderClearIndicator,
            Option: (props) => <Option {...props} variant={variant} />,
        };

        const renderSelect = () => {
            const commonProps = {
                ref: selectRef,
                styles: !unstyled ? customStyles(variant, error) : customUnstyled(variant),
                onChange: handleChange,
                value: value,
                placeholder: props.placeholder,
                options: props.options,
                closeMenuOnSelect: !props.isMulti,
                hideSelectedOptions: showSelectedOptions || props.isMulti,
                components: commonComponents,
                menuPosition: props.menuPosition || "absolute",
                menuShouldScrollIntoView: true,
                menuPlacement: "auto",
                cacheOptions: props.cacheOptions,
                defaultOptions: props.defaultOptions,
                isDisabled: props.disabled,
                ...props,
            };

            if (!props.loadOptions) {
                return (
                    <div style={{ position: "relative" }} ref={containerRef}>
                        {props.isCreatable ? <CreatableSelect {...commonProps} /> : <Select {...commonProps} />}
                    </div>
                );
            }

            return (
                <div style={{ position: "relative" }} ref={containerRef}>
                    {props.isCreatable ? (
                        <CreatableAsyncPaginateComp
                            {...commonProps}
                            loadOptions={(inputValue, callback) => {
                                const wrappedCallback = (options) => {
                                    const sortedOptions = sortByExactMatch(options, inputValue);
                                    callback(sortedOptions);
                                };
                                return props.loadOptions(inputValue, wrappedCallback, 1, props.fn, props.setState);
                            }}
                        />
                    ) : (
                        <AsyncSelect
                            {...commonProps}
                            loadOptions={(inputValue, callback) => {
                                const wrappedCallback = (options) => {
                                    const sortedOptions = sortByExactMatch(options, inputValue);
                                    callback(sortedOptions);
                                };
                                return props.loadOptions(inputValue, wrappedCallback, 1, props.fn, props.setState);
                            }}
                        />
                    )}
                </div>
            );
        };
        //todo: dropdown açıkken scroll yapınca dropdown yukarı/aşağı iniyor.
        //todo: optionlar silmeye çalışırken hata veriyor. istek atıyor?
        //todo: seçilen taglerin stilleri düzeltilmeli
        //todo: normal select ile multi select arasında yükseklik farkları var
        return <div ref={containerRef}>{renderSelect()}</div>;
    }
);

export default CustomSelect;
